import type {
  AiFeedbackRating,
  HelpArticle,
  HelpArticleProposal,
  HelpArticleStatus,
  HelpDocPage,
  HelpStats,
  HelpTicketDetail,
  HelpTicketKind,
  HelpTicketPage,
  HelpTicketStatus,
  FeatureGrant,
  Invitation,
  Member,
  Organization,
  OrgProfile,
  OrgSettings,
  QuotaCredit,
  Role,
  StaffActivityPage,
  StaffCustomerPage,
  StaffCustomerStats,
  EnterpriseOffer,
  EnterpriseContractInput,
  StaffMember,
  StaffOrgDetail,
  StaffRole,
  StaffAiFeedbackPage,
  SupportAccessGrant,
  SystemIssue,
  SystemIssueDetail,
  SystemIssueInvestigation,
  SystemIssuePage,
  SystemIssueSource,
  SystemIssueStatus,
} from "@backstory/protocol";
import { request, requestForm } from "../../api";

const enc = encodeURIComponent;

function messageForm(body: string, images: File[], extra?: Record<string, string | undefined>) {
  const form = new FormData();
  form.set("body", body);
  for (const [key, value] of Object.entries(extra ?? {})) {
    if (value) form.set(key, value);
  }
  for (const image of images) form.append("images", image);
  return form;
}

export type StripeCatalog = {
  products: number;
  prices: number;
  meters: number;
  meterPrices: number;
  lastSyncedAt?: string;
};

export type StaffBillingConfig = {
  configured: boolean;
  secretKeySet: boolean;
  webhookSecretSet: boolean;
  testSecretKeySet: boolean;
  testWebhookSecretSet: boolean;
  testConfigured: boolean;
  sandboxOrgId?: string;
  sandboxOrgName?: string;
  liveKeyMode?: "live" | "test" | "";
  source?: "database" | "environment";
  webhookUrl: string;
  testWebhookUrl: string;
  catalog: StripeCatalog;
  testCatalog: StripeCatalog;
};

export type CustomerFilters = { q?: string; plan?: string; status?: string };
export type ActivityFilters = { orgId?: string; actor?: string; action?: string; q?: string; sinceHours?: number };

export interface SystemIssueFilters {
  source?: SystemIssueSource;
  status: SystemIssueStatus | "all";
  sinceHours: number;
  q?: string;
  orgId?: string;
}

export interface ProductAvailability {
  open: boolean;
  pending: number;
  sent?: number;
  failed?: number;
  signups: { email: string; createdAt: string; notifiedAt?: string | null }[];
}

export type CapacityFact = { label: string; value: string };
export type CapacityResource = { name: string; state: string; detail?: string; items: CapacityFact[] };
export type CapacityReport = {
  at: string;
  machines: CapacityResource;
  database: CapacityResource;
  analytics: CapacityResource;
  recordings: CapacityResource;
  costs: CapacityResource;
};

export const staffApi = {
  /** Deployment AI capability, independent of any selected organization. */
  platformAiCapability: () => request<{ available: boolean }>(`/v1/staff/system/ai-capability`),
  capacity: () => request<CapacityReport>("/v1/staff/capacity"),
  orgs: (f: CustomerFilters = {}, cursor?: string, limit = 30) => {
    const p = new URLSearchParams();
    if (f.q) p.set("q", f.q);
    if (f.plan) p.set("plan", f.plan);
    if (f.status) p.set("status", f.status);
    if (cursor) p.set("cursor", cursor);
    p.set("limit", String(limit));
    return request<StaffCustomerPage>(`/v1/staff/orgs?${p.toString()}`);
  },
  customerStats: () => request<StaffCustomerStats>("/v1/staff/customer-stats"),
  org: (id: string) => request<StaffOrgDetail>(`/v1/staff/orgs/${enc(id)}`),
  deleteOrg: (id: string, confirmName: string) => request<{ deleted: boolean }>(`/v1/staff/orgs/${enc(id)}`, { method: "DELETE", body: { confirmName } }),
  patchOrg: (id: string, body: { name?: string; settings?: Partial<OrgSettings>; profile?: OrgProfile }) =>
    request<Organization>(`/v1/staff/orgs/${enc(id)}`, { method: "PATCH", body }),
  sendEnterpriseOffer: (id: string, body: EnterpriseContractInput) =>
    request<EnterpriseOffer>(`/v1/staff/orgs/${enc(id)}/enterprise`, { method: "POST", body }),
  setFeatures: (id: string, body: { features: string[]; note?: string }) =>
    request<{ features: string[]; note: string }>(`/v1/staff/orgs/${enc(id)}/features`, { method: "PUT", body }),
  addCredit: (id: string, body: { meter: string; units: number; expiresAt?: string; note: string }) =>
    request<QuotaCredit>(`/v1/staff/orgs/${enc(id)}/credits`, { method: "POST", body }),
  revokeCredit: (id: string, creditId: string) => request<QuotaCredit>(`/v1/staff/orgs/${enc(id)}/credits/${enc(creditId)}`, { method: "DELETE" }),
  addFeatureGrant: (id: string, body: { feature: string; expiresAt?: string; note: string }) =>
    request<FeatureGrant>(`/v1/staff/orgs/${enc(id)}/feature-grants`, { method: "POST", body }),
  revokeFeatureGrant: (id: string, grantId: string) =>
    request<FeatureGrant>(`/v1/staff/orgs/${enc(id)}/feature-grants/${enc(grantId)}`, { method: "DELETE" }),
  setOrgMemberRole: (id: string, userId: string, role: Role) =>
    request<Member[]>(`/v1/staff/orgs/${enc(id)}/members/${enc(userId)}`, { method: "PATCH", body: { role } }),
  removeOrgMember: (id: string, userId: string) => request<void>(`/v1/staff/orgs/${enc(id)}/members/${enc(userId)}`, { method: "DELETE" }),
  inviteOrgMember: (id: string, body: { email: string; role: Role }) =>
    request<Invitation>(`/v1/staff/orgs/${enc(id)}/invitations`, { method: "POST", body }),
  revokeOrgInvitation: (id: string, invitationId: string) =>
    request<void>(`/v1/staff/orgs/${enc(id)}/invitations/${enc(invitationId)}`, { method: "DELETE" }),
  activity: (f: ActivityFilters = {}, cursor?: string, limit = 50) => {
    const p = new URLSearchParams();
    if (f.orgId) p.set("orgId", f.orgId);
    if (f.actor) p.set("actor", f.actor);
    if (f.action) p.set("action", f.action);
    if (f.q) p.set("q", f.q);
    if (f.sinceHours != null) p.set("sinceHours", String(f.sinceHours));
    if (cursor) p.set("cursor", cursor);
    p.set("limit", String(limit));
    return request<StaffActivityPage>(`/v1/staff/activity?${p.toString()}`);
  },
  activityActors: () => request<{ items: string[] }>("/v1/staff/activity/actors"),
  members: () => request<{ items: StaffMember[] }>("/v1/staff/members"),
  addMember: (body: { email: string; role: StaffRole }) => request<StaffMember>("/v1/staff/members", { method: "POST", body }),
  setMemberRole: (userId: string, role: StaffRole) =>
    request<{ items: StaffMember[] }>(`/v1/staff/members/${enc(userId)}`, { method: "PATCH", body: { role } }),
  removeMember: (userId: string) => request<void>(`/v1/staff/members/${enc(userId)}`, { method: "DELETE" }),
  cancelInvitation: (email: string) => request<void>(`/v1/staff/invitations/${enc(email)}`, { method: "DELETE" }),
  product: () => request<ProductAvailability>("/v1/staff/product"),
  setProduct: (open: boolean) => request<ProductAvailability>("/v1/staff/product", { method: "PUT", body: { open } }),
  emailLaunch: (emails: string[]) => request<ProductAvailability>("/v1/staff/product/notify", { method: "POST", body: { emails } }),
  deleteNotify: (emails: string[]) => request<ProductAvailability>("/v1/staff/product/notify", { method: "DELETE", body: { emails } }),
  billingConfig: () => request<StaffBillingConfig>("/v1/staff/billing/config"),
  saveBillingConfig: (body: {
    secretKey?: string;
    webhookSecret?: string;
    testSecretKey?: string;
    testWebhookSecret?: string;
    sandboxOrgId?: string | null;
  }) => request<StaffBillingConfig>("/v1/staff/billing/config", { method: "PUT", body }),
  syncBillingCatalog: (mode: "live" | "test" = "live") =>
    request<StripeCatalog>("/v1/staff/billing/sync", { method: "POST", body: { mode } }),
  aiFeedback: (q: { rating: AiFeedbackRating | "all"; queue?: "open" | "acknowledged"; limit?: number; offset?: number }) => {
    const p = new URLSearchParams({ rating: q.rating });
    if (q.queue) p.set("queue", q.queue);
    if (q.limit) p.set("limit", String(q.limit));
    if (q.offset) p.set("offset", String(q.offset));
    return request<StaffAiFeedbackPage>(`/v1/staff/ai/feedback?${p.toString()}`);
  },
  acknowledgeAiFeedback: (artifactId: string) =>
    request<{ ok: boolean }>(`/v1/staff/ai/feedback/${enc(artifactId)}/acknowledge`, { method: "POST", body: {} }),
  reopenAiFeedback: (artifactId: string) =>
    request<{ ok: boolean }>(`/v1/staff/ai/feedback/${enc(artifactId)}/acknowledge`, { method: "DELETE" }),
  systemIssues: (q: SystemIssueFilters & { limit?: number; offset?: number }) => {
    const p = new URLSearchParams({ sinceHours: String(q.sinceHours), status: q.status });
    if (q.source) p.set("source", q.source);
    if (q.q) p.set("q", q.q);
    if (q.orgId) p.set("orgId", q.orgId);
    if (q.limit) p.set("limit", String(q.limit));
    if (q.offset) p.set("offset", String(q.offset));
    return request<SystemIssuePage>(`/v1/staff/system/issues?${p.toString()}`);
  },
  systemIssue: (id: string) => request<SystemIssueDetail>(`/v1/staff/system/issues/${enc(id)}`),
  systemIssueInvestigation: (id: string) =>
    request<SystemIssueInvestigation>(`/v1/staff/system/issues/${enc(id)}/investigation`),
  investigateSystemIssue: (id: string) =>
    request<SystemIssueInvestigation>(`/v1/staff/system/issues/${enc(id)}/investigation`, { method: "POST", body: {} }),
  acknowledgeSystemIssue: (id: string, note = "") =>
    request<SystemIssue>(`/v1/staff/system/issues/${enc(id)}/acknowledge`, { method: "POST", body: { note } }),
  reopenSystemIssue: (id: string) =>
    request<SystemIssue>(`/v1/staff/system/issues/${enc(id)}/acknowledge`, { method: "DELETE" }),
  requestAccess: (id: string, body: { reason: string; scopes?: string[]; ttlHours?: number }) =>
    request<SupportAccessGrant>(`/v1/staff/orgs/${enc(id)}/support-access`, { method: "POST", body }),
  cancelAccess: (orgId: string, grantId: string) =>
    request<void>(`/v1/staff/orgs/${enc(orgId)}/support-access/${enc(grantId)}`, { method: "DELETE" }),
  helpTickets: (q: { status?: string; kind?: string; q?: string; orgId?: string; limit?: number; offset?: number } = {}) => {
    const p = new URLSearchParams();
    if (q.status) p.set("status", q.status);
    if (q.kind) p.set("kind", q.kind);
    if (q.q) p.set("q", q.q);
    if (q.orgId) p.set("orgId", q.orgId);
    if (q.limit) p.set("limit", String(q.limit));
    if (q.offset) p.set("offset", String(q.offset));
    return request<HelpTicketPage>(`/v1/staff/help/tickets?${p.toString()}`);
  },
  helpStats: () => request<HelpStats>("/v1/staff/help/stats"),
  helpTicket: (id: string) => request<HelpTicketDetail>(`/v1/staff/help/tickets/${enc(id)}`),
  helpReply: (id: string, body: string, images?: File[]) =>
    images?.length
      ? requestForm<HelpTicketDetail>(`/v1/staff/help/tickets/${enc(id)}/messages`, messageForm(body, images))
      : request<HelpTicketDetail>(`/v1/staff/help/tickets/${enc(id)}/messages`, { method: "POST", body: { body } }),
  helpStatus: (id: string, body: { status: HelpTicketStatus; kind?: HelpTicketKind }) =>
    request<HelpTicketDetail>(`/v1/staff/help/tickets/${enc(id)}/status`, { method: "POST", body }),
  helpBrief: (id: string) => request<HelpTicketDetail>(`/v1/staff/help/tickets/${enc(id)}/brief`, { method: "POST", body: {} }),
  helpSendDraft: (id: string, messageId: string, body?: string) =>
    request<HelpTicketDetail>(`/v1/staff/help/tickets/${enc(id)}/drafts/${enc(messageId)}/send`, { method: "POST", body: body ? { body } : {} }),
  helpDiscardDraft: (id: string, messageId: string) => request<HelpTicketDetail>(`/v1/staff/help/tickets/${enc(id)}/drafts/${enc(messageId)}`, { method: "DELETE" }),
  helpProposeArticle: (id: string) => request<{ proposal: HelpArticleProposal; sourceTicket: string }>(`/v1/staff/help/tickets/${enc(id)}/article`, { method: "POST", body: {} }),
  helpArticles: (status?: string) => request<{ items: HelpArticle[] }>(`/v1/staff/help/articles${status ? `?status=${enc(status)}` : ""}`),
  helpSaveArticle: (body: Partial<HelpArticle> & { title: string; question: string; body: string }) =>
    request<HelpArticle>(body.id ? `/v1/staff/help/articles/${enc(body.id)}` : "/v1/staff/help/articles", { method: body.id ? "PATCH" : "POST", body }),
  helpArticleStatus: (id: string, status: HelpArticleStatus) =>
    request<HelpArticle>(`/v1/staff/help/articles/${enc(id)}/status`, { method: "POST", body: { status } }),
  helpDocs: () => request<{ items: HelpDocPage[]; docsUrl: string }>("/v1/staff/help/docs"),
  helpReindex: () => request<{ indexed: number; items: HelpDocPage[] }>("/v1/staff/help/docs/reindex", { method: "POST", body: {} }),
  helpExcludeDoc: (id: string, excluded: boolean) =>
    request<{ id: string; excluded: boolean }>(`/v1/staff/help/docs/${enc(id)}/exclude`, { method: "POST", body: { excluded } }),
};

export const helpApi = {
  list: (org: string, q: { status?: string; kind?: string; q?: string; limit?: number; offset?: number } = {}) => {
    const p = new URLSearchParams();
    if (q.status) p.set("status", q.status);
    if (q.kind) p.set("kind", q.kind);
    if (q.q) p.set("q", q.q);
    if (q.limit) p.set("limit", String(q.limit));
    if (q.offset) p.set("offset", String(q.offset));
    return request<HelpTicketPage>(`/v1/orgs/${enc(org)}/help/tickets?${p.toString()}`);
  },
  stats: (org: string) => request<HelpStats>(`/v1/orgs/${enc(org)}/help/stats`),
  get: (org: string, id: string) => request<HelpTicketDetail>(`/v1/orgs/${enc(org)}/help/tickets/${enc(id)}`),
  create: (org: string, body: { kind: HelpTicketKind; title: string; body: string; pagePath?: string; pageUrl?: string; appVersion?: string; sessionId?: string; projectId?: string }, images?: File[]) =>
    images?.length
      ? requestForm<HelpTicketDetail>(`/v1/orgs/${enc(org)}/help/tickets`, messageForm(body.body, images, body))
      : request<HelpTicketDetail>(`/v1/orgs/${enc(org)}/help/tickets`, { method: "POST", body }),
  reply: (org: string, id: string, body: string, images?: File[]) =>
    images?.length
      ? requestForm<HelpTicketDetail>(`/v1/orgs/${enc(org)}/help/tickets/${enc(id)}/messages`, messageForm(body, images))
      : request<HelpTicketDetail>(`/v1/orgs/${enc(org)}/help/tickets/${enc(id)}/messages`, { method: "POST", body: { body } }),
  feedback: (org: string, id: string, helpful: boolean, note?: string) =>
    request<HelpTicketDetail>(`/v1/orgs/${enc(org)}/help/tickets/${enc(id)}/feedback`, { method: "POST", body: { helpful, note } }),
};

export const supportAccessApi = {
  list: (org: string) => request<{ items: SupportAccessGrant[] }>(`/v1/orgs/${enc(org)}/support-access`),
  accept: (org: string, id: string, confirmUnmask?: boolean) =>
    request<SupportAccessGrant>(`/v1/orgs/${enc(org)}/support-access/${enc(id)}/accept`, { method: "POST", body: { confirmUnmask } }),
  deny: (org: string, id: string) => request<SupportAccessGrant>(`/v1/orgs/${enc(org)}/support-access/${enc(id)}/deny`, { method: "POST", body: {} }),
  revoke: (org: string, id: string) => request<SupportAccessGrant>(`/v1/orgs/${enc(org)}/support-access/${enc(id)}/revoke`, { method: "POST", body: {} }),
};

export const staffKeys = {
  orgs: (f: CustomerFilters) => ["staff", "orgs", f.q ?? "", f.plan ?? "", f.status ?? ""] as const,
  customerStats: ["staff", "customer-stats"] as const,
  org: (id: string) => ["staff", "org", id] as const,
  activity: (f: ActivityFilters) => ["staff", "activity", f.orgId ?? "", f.actor ?? "", f.action ?? "", f.q ?? "", f.sinceHours ?? 0] as const,
  activityActors: ["staff", "activity-actors"] as const,
  members: ["staff", "members"] as const,
  product: ["staff", "product"] as const,
  capacity: ["staff", "capacity"] as const,
  billingConfig: ["staff", "billing-config"] as const,
  aiFeedback: (filter: AiFeedbackRating | "all" | "acknowledged") => ["staff", "ai-feedback", filter] as const,
  systemIssues: (f: SystemIssueFilters) => ["staff", "system-issues", f.source ?? "", f.status, f.sinceHours, f.q ?? "", f.orgId ?? ""] as const,
  systemIssue: (id: string) => ["staff", "system-issue", id] as const,
  systemIssueInvestigation: (id: string) => ["staff", "system-issue-investigation", id] as const,
  grants: (org: string) => ["support-access", org] as const,
  help: (f: { status?: string; kind?: string; q?: string; orgId?: string }) => ["staff", "help", f.status ?? "", f.kind ?? "", f.q ?? "", f.orgId ?? ""] as const,
  helpStats: ["staff", "help", "stats"] as const,
  helpTicket: (id: string) => ["staff", "help-ticket", id] as const,
  helpArticles: ["staff", "help-articles"] as const,
  helpDocs: ["staff", "help-docs"] as const,
};

export const helpKeys = {
  list: (org: string) => ["help", org] as const,
  listFiltered: (org: string, f: { status?: string; q?: string }) => ["help", org, "list", f.status ?? "", f.q ?? ""] as const,
  stats: (org: string) => ["help", org, "stats"] as const,
  ticket: (org: string, id: string) => ["help", org, id] as const,
};
